import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapPin, ArrowRight, ArrowLeft, Building2, Home, Factory, Landmark } from 'lucide-react';

import api from '@/services/api';
import { projectImageAlt } from '@/lib/seo';
import { BlueprintGrid } from '@/components/ui/BlueprintGrid';
import residentialImg from '@/assets/residential-project.jpg';
import commercialImg from '@/assets/commercial-project.jpg';
import industrialImg from '@/assets/industrial-project.jpg';

const NAVY = '#071D3D';
const CARD_BG = '#0F2A4D';
const ACCENT = '#FFAA00';
const BODY = '#AEBBD1';
const BORDER = 'rgba(255,255,255,0.1)';

type Category = 'Ongoing Projects' | 'Completed Projects';

const categories: Category[] = ['Ongoing Projects', 'Completed Projects'];



function inferProjectCategory(project: any): Category {
  const statusSource = `${project?.status || ''} ${project?.project_status || ''} ${project?.category || ''}`.toLowerCase();
  if (/ongoing|in progress|in-progress|active|running/.test(statusSource)) return 'Ongoing Projects';
  if (/complete|completed|done|finished|handover/.test(statusSource)) return 'Completed Projects';

  const yearMatch = String(project?.year || '').match(/\b(20\d{2})\b/);
  if (yearMatch) {
    const yearValue = Number(yearMatch[1]);
    return yearValue >= new Date().getFullYear() ? 'Ongoing Projects' : 'Completed Projects';
  }

  return 'Completed Projects';
}

async function fetchProjects() {
  const response = await api.get('projects/');
  return response.data.map((p: any) => ({
    ...p,
    image: p.thumbnail || (Array.isArray(p.images) && p.images.length ? p.images[0] : ''),
    slug: p.slug || p.id,
  }));
}

function getCategoryIcon(category: string) {
  const c = category?.toLowerCase() || '';
  if (c.includes('residential') || c.includes('house')) return Home;
  if (c.includes('commercial') || c.includes('office') || c.includes('shop')) return Building2;
  if (c.includes('industrial') || c.includes('factory') || c.includes('store')) return Factory;
  return Landmark;
}

export function LatestProjectsSection() {
  const [activeCategory, setActiveCategory] = useState<Category>('Completed Projects');
  const [page, setPage] = useState(0);

  const { data: projects, refetch } = useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjects
  });

  useEffect(() => {
    const handleFocus = () => refetch();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetch]);

  useEffect(() => {
    setPage(0);
  }, [activeCategory]);

  const realProjects = projects ?? [];
  const filtered = realProjects.filter((p: any) => inferProjectCategory(p) === activeCategory);
  const source = filtered;

  const totalPages = Math.max(1, Math.ceil(source.length / 3));
  const currentPage = Math.min(page, totalPages - 1);
  const visibleProjects = source.slice(currentPage * 3, currentPage * 3 + 3);

  return (
    <section className="relative py-9 md:py-14 overflow-hidden" style={{ background: `linear-gradient(160deg, ${NAVY} 0%, #061A38 100%)` }}>
      <BlueprintGrid />

      {/* Subtle crane silhouette, right side only */}
      <img
        src="/assets/Improve%20tomorrow%20sec%20back.png"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover opacity-[0.08]"
        style={{ maskImage: 'linear-gradient(to left, black 0%, transparent 55%)', WebkitMaskImage: 'linear-gradient(to left, black 0%, transparent 55%)' }}
      />

      {/* Soft corner glow */}
      <div
        className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, rgba(255,170,0,0.18), transparent 70%)` }}
      />

      {/* Blueprint-style corner brackets */}
      <svg className="absolute bottom-6 left-6 w-14 h-14 pointer-events-none" viewBox="0 0 56 56" fill="none" aria-hidden="true">
        <path d="M2 30 L2 54 L26 54" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" opacity="0.7" />
      </svg>
      <svg className="absolute top-6 right-6 w-14 h-14 pointer-events-none" viewBox="0 0 56 56" fill="none" aria-hidden="true">
        <path d="M54 26 L54 2 L30 2" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" opacity="0.35" />
      </svg>

      <div className="max-w-325 mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
        {/* Header + Filter Row */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5 mb-5 md:mb-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-2.5 mb-1.5">
              <span className="w-6 h-px" style={{ backgroundColor: ACCENT }} />
              <span className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: ACCENT }}>Our Projects</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black leading-tight mb-1.5 text-white">
              Building Excellence<br />
              Through <span style={{ color: ACCENT }}>Every Project</span>
            </h2>
            <p className="max-w-xl text-[13px] leading-relaxed" style={{ color: BODY }}>
              Explore our selected construction projects across residential, commercial and industrial sectors, built with quality, precision and care.
            </p>
          </motion.div>

          {/* Category Filter */}
          <div
            className="inline-flex items-center gap-1 rounded-full p-1 self-start lg:mt-1"
            style={{ border: `1px solid ${BORDER}` }}
          >
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className="px-3.5 sm:px-4 py-1.5 rounded-full text-xs font-bold transition-all duration-300 whitespace-nowrap"
                style={
                  activeCategory === cat
                    ? { backgroundColor: ACCENT, color: NAVY }
                    : { backgroundColor: 'transparent', color: '#FFFFFF' }
                }
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Project Grid */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${activeCategory}-${currentPage}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className={visibleProjects.length === 0 ? "flex flex-col items-center justify-center text-center py-16 bg-[#0F2A4D] border border-white/10 rounded-2xl w-full col-span-full h-48" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"}
            >
              {visibleProjects.length === 0 ? (
                <>
                  <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center text-[#FFAA00] mb-4">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-1">Projects Coming Soon</h3>
                  <p className="text-[#AEBBD1] text-sm">No projects found in this category.</p>
                </>
              ) : (
                visibleProjects.map((project: any, index: number) => {
                  const CategoryIcon = getCategoryIcon(project.category || project.type || '');
                  const detailHref = project.isPlaceholder ? '/portfolio' : `/portfolio/${project.slug || project.id}`;

                  return (
                    <motion.div
                      key={project.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.08 }}
                      className="rounded-2xl overflow-hidden flex flex-col transition-colors duration-300 hover:border-amber-400/40"
                      style={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}` }}
                    >
                      {/* Image */}
                      <div className="relative h-28 sm:h-32 overflow-hidden">
                        <img
                          src={project.image}
                          alt={projectImageAlt(project.title, project.location, 'project card')}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        <div
                          className="absolute top-2.5 left-2.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide"
                          style={{ backgroundColor: ACCENT, color: NAVY }}
                        >
                          <CategoryIcon className="w-3 h-3" />
                          {project.category || 'Construction'}
                        </div>
                      </div>

                      {/* Body */}
                      <div className="p-3 flex flex-col flex-1">
                        <h3 className="font-bold text-white text-[15px] leading-snug mb-0.5">{project.title}</h3>
                        {project.location && (
                          <p className="flex items-center gap-1.5 text-[12px] mb-1" style={{ color: BODY }}>
                            <MapPin className="w-3.5 h-3.5 shrink-0" style={{ color: ACCENT }} />
                            {project.location}
                          </p>
                        )}
                        <p className="text-[12px] leading-snug mb-2 flex-1 line-clamp-2" style={{ color: BODY }}>
                          {project.description || 'A quality-focused construction project delivered with precision and care.'}
                        </p>
                        <Link
                          to={detailHref}
                          className="inline-flex items-center gap-1.5 text-[13px] font-bold transition-all duration-300 hover:gap-2.5 self-start"
                          style={{ color: ACCENT }}
                        >
                          View Project <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </motion.div>
          </AnimatePresence>

          {/* Carousel arrows + pagination, only when there's more than one page */}
          {totalPages > 1 && (
            <>
              <button
                onClick={() => setPage((p) => (p - 1 + totalPages) % totalPages)}
                aria-label="Previous projects"
                className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center transition-colors hover:border-amber-400/50"
                style={{ backgroundColor: NAVY, border: `1px solid ${BORDER}` }}
              >
                <ArrowLeft className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={() => setPage((p) => (p + 1) % totalPages)}
                aria-label="Next projects"
                className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center transition-colors hover:border-amber-400/50"
                style={{ backgroundColor: NAVY, border: `1px solid ${BORDER}` }}
              >
                <ArrowRight className="w-4 h-4 text-white" />
              </button>

              <div className="flex items-center justify-center gap-3 mt-5">
                <span className="text-xs font-semibold" style={{ color: BODY }}>
                  {String(currentPage + 1).padStart(2, '0')} / {String(totalPages).padStart(2, '0')}
                </span>
                <div className="w-20 h-0.5 rounded-full overflow-hidden" style={{ backgroundColor: BORDER }}>
                  <div
                    className="h-full transition-all duration-300"
                    style={{ backgroundColor: ACCENT, width: `${((currentPage + 1) / totalPages) * 100}%` }}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* View All CTA */}
        <div className="flex justify-center mt-5 md:mt-6">
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 font-bold text-sm px-6 py-2.5 rounded-full border transition-all duration-300 hover:gap-3"
            style={{ borderColor: ACCENT, color: ACCENT }}
          >
            View All Projects <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
